import { ParamMap } from '@angular/router';

import { URIPrefix } from '../enums/url-prefix.enum';
import { UrlQueryParams } from '../enums/url-query-params.enum';

/** Regex: "." only when followed by a category prefix (so values may contain "."). */
const prefixPattern = Object.values(URIPrefix).join('|');
const CATEGORY_DELIMITER_REGEX = new RegExp(`\\.(?=${prefixPattern}-)`);

export interface IFilterEntry {
  key: string;
  value: string;
}

/**
 * Parses a filter string into categorized key-value entries.
 * Preserves category grouping (`.`-delimited) and intra-category ordering (`,`-delimited).
 * Splits on the first `-` only, so values like "Non-binary" or "EAP (CO)" are kept intact.
 */
export const parseFilterEntries = (filterString: string): IFilterEntry[][] => filterString
  .split(CATEGORY_DELIMITER_REGEX)
  .map((segment) => segment.split(',').reduce<IFilterEntry[]>((entries, kvp) => {
    const dashIdx = kvp.indexOf('-');
    if (dashIdx <= 0) return entries;

    const key = kvp.substring(0, dashIdx);
    const value = kvp.substring(dashIdx + 1);
    if (value) {
      entries.push({ key, value });
    }
    return entries;
  }, []));

/* eslint-disable no-restricted-syntax */
export type DirectoryParsedParams = {
  [URIPrefix.STATE]: string | null;
  [URIPrefix.CITY]: string | null;
  [URIPrefix.INSURANCE]: string;
  [URIPrefix.MODALITY]: string[];
  [URIPrefix.SPECIALTY]: string[];
  [URIPrefix.GENDER]: string[];
  [URIPrefix.TREATMENT_PREFERENCE]: string[];
  [URIPrefix.AGE]: string[];
  [URIPrefix.SESSION_TYPE]: string[];
};

export const ParseDirectoryFilterParams = (paramMap: ParamMap): DirectoryParsedParams => {
  // example URI - /sta-CO/cty-all/ins-aetna;mod-group,mod-family;spc-depression,spc-anxiety;gdr-man
  const state = paramMap.get(UrlQueryParams.STATE)?.split('-')[1]?.toUpperCase();
  const city = paramMap.get(UrlQueryParams.CITY)?.split('-')[1];
  const params: DirectoryParsedParams = {
    [URIPrefix.STATE]: state || null,
    [URIPrefix.CITY]: city ? decodeURIComponent(city) : null,
    [URIPrefix.INSURANCE]: '',
    [URIPrefix.MODALITY]: [],
    [URIPrefix.SPECIALTY]: [],
    [URIPrefix.GENDER]: [],
    [URIPrefix.TREATMENT_PREFERENCE]: [],
    [URIPrefix.AGE]: [],
    [URIPrefix.SESSION_TYPE]: []
  };
  const filters = paramMap.get(UrlQueryParams.FILTERS);
  if (filters) {
    parseFilterEntries(filters).forEach((entries) => {
      entries.forEach(({ key, value }) => {
        if (key === URIPrefix.INSURANCE) {
          params[URIPrefix.INSURANCE] = decodeURIComponent(value);
        } else if (
          [
            URIPrefix.MODALITY,
            URIPrefix.SPECIALTY,
            URIPrefix.GENDER,
            URIPrefix.SESSION_TYPE,
            URIPrefix.AGE,
            URIPrefix.TREATMENT_PREFERENCE
          ].includes(key as URIPrefix)
        ) {
          (params[key as URIPrefix] as string[]).push(decodeURIComponent(value));
        }
      });
    });
  }
  return params;
};

export const ValidateDirectoryParams = (input: string): boolean => {
  // Early return for empty string (all categories are optional)
  if (!input || input.trim() === '') {
    return true;
  }

  // Split on "." only when followed by a category prefix, so "." is allowed in values
  const categories = input.split(CATEGORY_DELIMITER_REGEX).filter((cat) => cat.trim() !== '');

  // Define patterns for each category
  const patterns = {
    ins: /^ins-.+$/,
    mod: /^mod-.+$/,
    spc: /^spc-.+$/,
    gdr: /^gdr-.+$/,
    tap: /^tap-.+$/,
    age: /^age-.+$/,
    sess: /^sess-.+$/
  };

  // Keep track of categories encountered
  const categoryCount: { [key: string]: number; } = {};

  for (const category of categories) {
    // Split category items by commas
    const items = category.split(',').filter((item) => item.trim() !== '');

    if (items.length === 0) {
      return false; // Empty category is invalid
    }

    // Determine the category type based on the prefix of the first item
    const firstItem = items[0].trim();
    let categoryType: string | null = null;

    for (const key in patterns) {
      if (patterns[key as keyof typeof patterns].test(firstItem)) {
        categoryType = key;
        break;
      }
    }

    if (!categoryType) {
      // Invalid category type
      return false;
    }

    // Update category count
    categoryCount[categoryType] = (categoryCount[categoryType] || 0) + 1;

    // Check for multiple ins-* categories
    if (categoryType === 'ins' && categoryCount['ins'] > 1) {
      return false; // Only one ins-* category allowed
    }

    // Validate each item in the category
    const pattern = patterns[categoryType as keyof typeof patterns];
    for (const item of items) {
      if (!pattern.test(item.trim())) {
        return false; // Item does not match the required pattern
      }
    }
  }

  return true;
};
